import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users } from "lucide-react";

const STATUS_BADGE = {
  healthy:  "bg-emerald-100 text-emerald-700 border-emerald-200",
  warning:  "bg-amber-100 text-amber-700 border-amber-200",
  degraded: "bg-orange-100 text-orange-700 border-orange-200",
  critical: "bg-red-100 text-red-700 border-red-200",
  unknown:  "bg-gray-100 text-gray-600 border-gray-200",
};

const COLS = [
  "Tenant", "Status", "Score", "Modo",
  "QR", "Zombie", "ACK↓", "Reconn.",
  "Fila", "Taxa Envio", "Erros", "Último Evento",
];

function pct(v)  { return v == null ? "—" : `${Math.round(v * 100)}%`; }
function val(v)  { return v ?? "—"; }
function relTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function BoolDot({ value, danger }) {
  if (!value) return <span className="text-gray-300">—</span>;
  return <span className={danger ? "text-red-600 font-bold" : "text-amber-500"}>●</span>;
}

function EmptyState() {
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <Users className="w-8 h-8 text-gray-300 mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">Nenhum tenant neste snapshot.</p>
        <p className="text-xs text-muted-foreground mt-1">
          Válido quando o token não tem escopo de tenant ou nenhum evento foi registrado.
        </p>
      </CardContent>
    </Card>
  );
}

export default function TenantTable({ tenants }) {
  if (!tenants.length) return <EmptyState />;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold">Tenants ({tenants.length})</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="border-b bg-gray-50">
              <tr>
                {COLS.map(h => (
                  <th key={h} className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {tenants.map(t => {
                const cls = STATUS_BADGE[t.health_status] || STATUS_BADGE.unknown;
                return (
                  <tr key={t.tenant_id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium whitespace-nowrap">
                      <Link to={`/command-center/${t.tenant_id}`} className="hover:underline text-blue-700">
                        {t.display_name || t.tenant_id}
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      <Badge className={`text-[10px] border ${cls}`}>{t.health_status || "unknown"}</Badge>
                    </td>
                    <td className="px-3 py-2">{val(t.health_score)}</td>
                    <td className="px-3 py-2">{val(t.effective_mode)}</td>
                    <td className="px-3 py-2"><BoolDot value={t.pending_qr}       danger /></td>
                    <td className="px-3 py-2"><BoolDot value={t.suspected_zombie} danger /></td>
                    <td className="px-3 py-2"><BoolDot value={t.ack_degraded}            /></td>
                    <td className="px-3 py-2"><BoolDot value={t.reconnect_loop}          /></td>
                    <td className="px-3 py-2">{val(t.queue_depth)}</td>
                    <td className="px-3 py-2">{pct(t.send_success_rate)}</td>
                    <td className="px-3 py-2">{val(t.recent_error_count)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{relTime(t.last_event_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
